import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger);

const root = document.documentElement;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = window.matchMedia('(pointer: fine)').matches;

if (reduceMotion) root.classList.add('no-motion');

/* Smooth scroll */

let lenis = null;

if (!reduceMotion) {
  lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);
}

function scrollTo(target) {
  if (lenis) lenis.scrollTo(target, { offset: -8, duration: 1.4 });
  else target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
}

document.querySelectorAll('[data-scroll]').forEach((link) => {
  link.addEventListener('click', (event) => {
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    event.preventDefault();
    scrollTo(target);
    history.replaceState(null, '', link.getAttribute('href'));
  });
});

/* Clock in Garden City */

const clock = document.getElementById('clock');
const timeFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour: 'numeric',
  minute: '2-digit',
});

function tick() {
  if (clock) clock.textContent = timeFormat.format(new Date());
}

tick();
setInterval(tick, 15000);

/* Entrance */

const nav = document.getElementById('nav');

if (reduceMotion) {
  nav.classList.add('is-ready');
} else {
  const intro = gsap.timeline({ defaults: { ease: 'power4.out' } });
  intro
    .to('.hero-top .line-inner', { y: 0, duration: 1, stagger: 0.08 }, 0.1)
    .to('.hero-name .line-inner', { y: 0, duration: 1.3 }, 0.2)
    .to('.hero-statement .line-inner', { y: 0, duration: 1, stagger: 0.07 }, 0.55)
    .to('.hero-scroll .line-inner', { y: 0, duration: 0.8 }, 0.9)
    .add(() => nav.classList.add('is-ready'), 0.7);
}

/* Hero drifts up and fades as you leave it */

if (!reduceMotion) {
  gsap.to('.hero-name', {
    yPercent: -18,
    opacity: 0.15,
    ease: 'none',
    scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true },
  });
  gsap.to('.hero-glow', {
    yPercent: 30,
    ease: 'none',
    scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true },
  });
}

/* Reveals */

if (!reduceMotion) {
  ScrollTrigger.batch('.row', {
    start: 'top 92%',
    once: true,
    onEnter: (batch) =>
      gsap.to(batch, { opacity: 1, y: 0, duration: 0.9, stagger: 0.09, ease: 'power3.out', overwrite: true }),
  });

  gsap.utils.toArray('[data-reveal]').forEach((el) => {
    gsap.from(el, {
      opacity: 0,
      y: 24,
      duration: 0.9,
      ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 90%', once: true },
    });
  });
}

/* Count-ups */

gsap.utils.toArray('[data-count]').forEach((el) => {
  const target = Number(el.dataset.count);
  if (reduceMotion) {
    el.textContent = target;
    return;
  }
  const state = { value: 0 };
  gsap.to(state, {
    value: target,
    duration: 1.8,
    ease: 'power2.out',
    scrollTrigger: { trigger: el, start: 'top 85%', once: true },
    onUpdate: () => {
      el.textContent = Math.round(state.value);
    },
  });
});

/* Scroll progress and nav hide on scroll down */

const progressBar = document.getElementById('progress-bar');
let lastY = 0;

ScrollTrigger.create({
  start: 0,
  end: 'max',
  onUpdate: (self) => {
    progressBar.style.transform = `scaleX(${self.progress})`;
    const y = self.scroll();
    const goingDown = y > lastY + 4;
    const goingUp = y < lastY - 4;
    if (goingDown && y > 160) nav.classList.add('is-hidden');
    else if (goingUp) nav.classList.remove('is-hidden');
    lastY = y;
  },
});

/* Current section in the nav */

const navLinks = [...document.querySelectorAll('.nav-links a[href^="#"]')];
navLinks.forEach((link) => {
  const section = document.querySelector(link.getAttribute('href'));
  if (!section) return;
  ScrollTrigger.create({
    trigger: section,
    start: 'top 45%',
    end: 'bottom 45%',
    onToggle: (self) => {
      if (self.isActive) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    },
  });
});

/* Pointer-driven effects: only for mice and trackpads, never with reduced motion */

if (finePointer && !reduceMotion) {
  root.classList.add('has-cursor');

  const cursor = document.getElementById('cursor');
  const ring = document.getElementById('cursor-ring');
  const label = document.getElementById('cursor-label');
  const preview = document.getElementById('preview');
  const previewImg = document.getElementById('preview-img');

  const dotX = gsap.quickTo(cursor, 'x', { duration: 0.12, ease: 'power3' });
  const dotY = gsap.quickTo(cursor, 'y', { duration: 0.12, ease: 'power3' });
  const ringX = gsap.quickTo(ring, 'x', { duration: 0.45, ease: 'power3' });
  const ringY = gsap.quickTo(ring, 'y', { duration: 0.45, ease: 'power3' });
  const previewX = gsap.quickTo(preview, 'x', { duration: 0.6, ease: 'power3' });
  const previewY = gsap.quickTo(preview, 'y', { duration: 0.6, ease: 'power3' });
  const previewRotate = gsap.quickTo(preview, 'rotation', { duration: 0.6, ease: 'power3' });

  let lastX = 0;
  let visible = false;

  gsap.set(cursor, { xPercent: 0, yPercent: 0, opacity: 0 });

  window.addEventListener('pointermove', (event) => {
    const { clientX: x, clientY: y } = event;
    if (!visible) {
      visible = true;
      gsap.set(cursor, { x, y });
      gsap.set(ring, { x: 0, y: 0 });
      gsap.to(cursor, { opacity: 1, duration: 0.3 });
    }
    dotX(x);
    dotY(y);
    ringX(0);
    ringY(0);
    previewX(x + 40);
    previewY(y + 30);
    previewRotate(gsap.utils.clamp(-8, 8, (x - lastX) * 0.4));
    lastX = x;
  });

  document.addEventListener('mouseleave', () => {
    visible = false;
    gsap.to(cursor, { opacity: 0, duration: 0.3 });
  });

  const hoverables = document.querySelectorAll('a, button, [data-cursor]');
  hoverables.forEach((el) => {
    el.addEventListener('mouseenter', () => {
      const text = el.dataset.cursor;
      cursor.classList.add('is-active');
      if (text) {
        label.textContent = text;
        cursor.classList.add('has-label');
        gsap.to(ring, { scale: 2.4, duration: 0.4, ease: 'power3.out' });
      } else {
        gsap.to(ring, { scale: 1.6, duration: 0.4, ease: 'power3.out' });
      }
    });
    el.addEventListener('mouseleave', () => {
      cursor.classList.remove('is-active', 'has-label');
      gsap.to(ring, { scale: 1, duration: 0.4, ease: 'power3.out' });
    });
  });

  /* Project previews */

  document.querySelectorAll('.project[data-preview]').forEach((project) => {
    project.addEventListener('mouseenter', () => {
      previewImg.src = project.dataset.preview;
      const ratio = project.dataset.previewRatio || '16/10';
      const [w, h] = ratio.split('/').map(Number);
      preview.style.aspectRatio = ratio;
      preview.classList.toggle('is-portrait', h > w);
      gsap.to(preview, { opacity: 1, scale: 1, duration: 0.5, ease: 'power3.out', overwrite: true });
    });
    project.addEventListener('mouseleave', () => {
      gsap.to(preview, { opacity: 0, scale: 0.9, duration: 0.4, ease: 'power3.out', overwrite: true });
    });
  });

  /* Magnetic email */

  document.querySelectorAll('[data-magnetic]').forEach((el) => {
    const toX = gsap.quickTo(el, 'x', { duration: 0.5, ease: 'power3' });
    const toY = gsap.quickTo(el, 'y', { duration: 0.5, ease: 'power3' });
    el.addEventListener('mousemove', (event) => {
      const rect = el.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);
      toX(dx * 0.18);
      toY(dy * 0.3);
    });
    el.addEventListener('mouseleave', () => {
      gsap.to(el, { x: 0, y: 0, duration: 0.8, ease: 'elastic.out(1, 0.5)' });
    });
  });
}

/* Duplicate the ticker so it loops seamlessly */

const track = document.getElementById('ticker-track');
if (track) {
  [...track.children].forEach((item) => {
    const copy = item.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    track.appendChild(copy);
  });
}

/* Recalculate after fonts and images settle */

window.addEventListener('load', () => ScrollTrigger.refresh());
